import React, { useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ConversationList from './ConversationList';
import ToolExecutionPanel from './ToolExecutionPanel';
import ConfirmationDialog from './ConfirmationDialog';
import { AlertCircleIcon } from './Icons';
import type { ExecutionStep, PendingAction } from '../services/api/agent/types';
import type { MessageSource } from '../services/gateway/types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string | Date;
  source?: MessageSource;
}

interface Conversation {
  id: string;
  name: string;
  lastMessage?: string;
  updatedAt: string | Date;
}

interface ChatContainerProps {
  messages: Message[];
  conversations: Conversation[];
  activeConversationId?: string;
  isLoading?: boolean;
  error?: string | null;
  showConversationList?: boolean;
  executingSteps?: ExecutionStep[];
  pendingAction?: PendingAction | null;
  onSendMessage: (content: string, attachments?: File[]) => void;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onImportFromDevice?: () => void;
  onRenameConversation?: (id: string, newName: string) => void;
  onDeleteConversation?: (id: string) => void;
  onFeedback?: (messageId: string, feedback: 'like' | 'dislike' | null) => void;
  onDismissError?: () => void;
  onConfirmAction?: (confirmed: boolean, confirmationCode?: string) => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  conversations,
  activeConversationId,
  isLoading = false,
  error = null,
  showConversationList = true,
  executingSteps = [],
  pendingAction = null,
  onSendMessage,
  onSelectConversation,
  onNewChat,
  onRenameConversation,
  onDeleteConversation,
  onFeedback,
  onDismissError,
  onConfirmAction,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  };

  const chatAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#2B3B44',
  };

  const messagesContainerStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 0',
  };

  const errorBannerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'rgba(255,86,86,0.15)',
    borderBottom: '1px solid rgba(255,86,86,0.3)',
    color: '#FF5656',
    fontSize: '13px',
  };

  const errorDismissStyle: React.CSSProperties = {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: '#FF5656',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '0 4px',
  };

  const emptyStateStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '8px',
  };

  const emptySubtitleStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#8899A6',
    lineHeight: '1.5',
  };

  const loadingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    color: '#8899A6',
    fontSize: '13px',
  };

  const segmentMarkerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#8899A6',
    fontSize: '11px',
    padding: '10px 16px 4px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  };

  const segmentLineStyle: React.CSSProperties = {
    flex: 1,
    height: '1px',
    backgroundColor: 'rgba(255,255,255,0.12)',
  };

  const segmentLabel = (msg: Message, prev?: Message): string => {
    if (msg.source === 'fallback' && prev?.source !== 'fallback') return 'Switched to offline mode';
    if (msg.source === 'gateway' && prev?.source === 'fallback') return 'Reconnected to Gateway';
    if (msg.source === 'fallback') return 'Fallback Segment';
    if (msg.source === 'gateway') return 'Gateway Segment';
    return 'Conversation';
  };

  const spinnerStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.1)',
    borderTopColor: '#33AA40',
    borderRadius: '50%',
    animation: 'equanaut-spin 0.8s linear infinite',
  };

  return (
    <>
      <style>
        {`
          @keyframes equanaut-spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={containerStyle}>
        {showConversationList && (
          <ConversationList
            conversations={conversations}
            activeConversationId={activeConversationId}
            onSelectConversation={onSelectConversation}
            onNewChat={onNewChat}
            onImportFromDevice={onImportFromDevice}
            onRenameConversation={onRenameConversation}
            onDeleteConversation={onDeleteConversation}
          />
        )}
        <div style={chatAreaStyle}>
          {pendingAction && onConfirmAction && (
            <ConfirmationDialog
              action={pendingAction}
              onConfirm={(code) => onConfirmAction(true, code)}
              onCancel={() => onConfirmAction(false)}
              isLoading={isLoading}
            />
          )}
          {executingSteps.length > 0 && (
            <ToolExecutionPanel steps={executingSteps} />
          )}
          {error && (
            <div style={errorBannerStyle}>
              <AlertCircleIcon size={16} color="#FF5656" />
              <span>{error}</span>
              {onDismissError && (
                <button style={errorDismissStyle} onClick={onDismissError}>
                  ×
                </button>
              )}
            </div>
          )}
          <div style={messagesContainerStyle}>
            {messages.length === 0 ? (
              <div style={emptyStateStyle}>
                <div style={emptyTitleStyle}>Welcome to Equanaut</div>
                <div style={emptySubtitleStyle}>
                  Your AI assistant for Equa platform.<br />
                  Ask a question to get started.
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => {
                  const prev = idx > 0 ? messages[idx - 1] : undefined;
                  const isTransition = idx === 0 || msg.source !== prev?.source;
                  const isSystemBoundary = /^system-(?:fallback|reconnect)-/.test(msg.id);
                  return (
                    <React.Fragment key={msg.id}>
                      {isTransition && (
                        <div style={segmentMarkerStyle}>
                          <div style={segmentLineStyle} />
                          <span>{segmentLabel(msg, prev)}</span>
                          <div style={segmentLineStyle} />
                        </div>
                      )}
                      {!isSystemBoundary && <ChatMessage message={msg} onFeedback={onFeedback} />}
                    </React.Fragment>
                  );
                })}
                {isLoading && (
                  <div style={loadingStyle}>
                    <div style={spinnerStyle} />
                    <span>Equanaut is thinking...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
          <ChatInput
            onSendMessage={onSendMessage}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ChatContainer;
